/**
 * useRecaptchaToken.ts
 *
 * Custom hook for acquiring Google reCAPTCHA v3 tokens.
 * Centralises the executeRecaptcha null-check and error toast so each auth
 * page gets a single getToken(action) call instead of repeating the guard.
 *
 * @version 1.0.0
 * @author Frontend Team
 * @date 2026-04-07
 * @modified 2026-04-07
 * @copyright © 2026 Thesis Pte Ltd. All rights reserved.
 */

import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { toast } from "react-toastify"
import { MESSENGER_NOTIFICATION } from "constants/messenger"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import * as Sentry from "@sentry/react"

export const useRecaptchaToken = () => {
  const { executeRecaptcha } = useGoogleReCaptcha()

  /**
   * Acquire a reCAPTCHA v3 token for the given action.
   * Shows an error toast and throws if the reCAPTCHA script is not yet loaded.
   */
  const getToken = async (action: string): Promise<string> => {
    if (!executeRecaptcha) {
      toast(
        <LabelNotificationPage
          messenger={MESSENGER_NOTIFICATION.RECAPTCHA_AVAILABLE}
          type="error"
        />
      )
      const err = new Error(MESSENGER_NOTIFICATION.RECAPTCHA_AVAILABLE)
      Sentry.captureException(err)
      throw err
    }
    return executeRecaptcha(action)
  }

  return { getToken }
}
